import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useMemo } from 'react';
import { ContractInput } from '../../context/types';

const monoCell = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: 13,
};

const MODALITY_LABELS: Record<string, string> = {
  mri: 'MRI',
  ct_scan: 'CT Scan',
  x_ray: 'X-Ray',
  ultrasound: 'Ultrasound',
};

const humanizeModality = (key: string): string =>
  MODALITY_LABELS[key.toLowerCase()] ??
  key
    .split('_')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

interface GlobalModalityDisplayProps {
  input: ContractInput;
}

export const GlobalModalityDisplay = ({
  input,
}: GlobalModalityDisplayProps): JSX.Element => {
  const grouped = useMemo(() => {
    const services = input.fee_schedule.services ?? {};
    return Object.entries(services).filter(
      (entry): entry is [string, NonNullable<(typeof entry)[1]>] =>
        entry[1] != null && entry[1].length > 0,
    );
  }, [input.fee_schedule.services]);

  return (
    <HHStack spacing={2}>
      <HHStack spacing={0.5}>
        <HHTypography hhVariant="subtitle1">Input reference</HHTypography>
        <HHTypography hhVariant="caption" sx={{ opacity: 0.65 }}>
          Modality rows from the input contract. Reference only — operators
          compose service lines below independently of this list.
        </HHTypography>
      </HHStack>

      {grouped.length === 0 ? (
        <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
          No modality rows in the input.
        </HHTypography>
      ) : (
        grouped.map(([modality, rows]) => (
          <HHStack spacing={1} key={modality}>
            <HHTypography hhVariant="subtitle2">
              {humanizeModality(modality)}
            </HHTypography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell sx={{ width: 140 }} align="right">
                      Global fee
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={`${modality}:${idx}:${r.description}`}>
                      <TableCell>{r.description}</TableCell>
                      <TableCell sx={monoCell} align="right">
                        ${r.global_fee.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </HHStack>
        ))
      )}
    </HHStack>
  );
};
